import { demoLocale } from "../constants";

/*
  Sample conversations for the Knot, String and Brane panels: a synthetic campus week,
  one set per locale, for the same reason the Loops demo's are
  (constants.ts): this is content standing in for a feed, not UI copy. Labels
  around it go through i18n.

  No figures and no institutions (PRODUCT.md, Claims Policy): the Knot names a
  course, not a university, and carries no member count.
*/

export interface DemoLine {
  who: string;
  text: string;
  own?: boolean;
}

export interface Demo {
  knot: { name: string; meta: string; lines: DemoLine[] };
  string: { them: string; lines: DemoLine[] };
  /** The String one sender opened through a Brane, seen by its owner ("you"), labelled with the Brane's emoji and name. */
  brane: { them: string; emoji: string; name: string; bio: string; lines: DemoLine[] };
}

const mn: Demo = {
  knot: {
    name: "Математик анализ · шалгалтын бэлтгэл",
    meta: "Нэрээрээ · Хүсэлтийг баталж оруулдаг",
    lines: [
      { who: "Номин", text: "Маргааш 9 цагт номын сангийн гуравдугаар давхарт суудал эзэлье. Өглөө −30 болно гэсэн." },
      { who: "Тэмүүлэн", text: "7-р автобус өглөө хоцорч байгаа. 8:30-аас өмнө гарвал амжина." },
      { who: "Ану", text: "4-р бодлогын бодолтыг зургаар нь явуулчихлаа. Хязгаарыг нь шалгаад өгөөч." },
      { who: "Та", text: "Шалгалтын дараа гуанзанд цуглах уу?", own: true },
    ],
  },
  string: {
    them: "Шөнийн шувуу",
    lines: [
      { who: "Шөнийн шувуу", text: "Манай байрны халаалт дахиад унтарчихлаа. Хөнжилдөө ороогоод хичээлээ хийж сууна." },
      { who: "Та", text: "Манайх ч гэсэн. Өглөө −30 байхад 7-р автобус хүлээх хамгийн хэцүү.", own: true },
      { who: "Шөнийн шувуу", text: "Наадмын амралт хүртэл тэвчье дээ." },
      { who: "Та", text: "Тэр үед хөдөө явна. Чи?", own: true },
    ],
  },
  brane: {
    them: "Саарал чоно",
    emoji: "🩺",
    name: "Анагаахын талаар асуу",
    bio: "Анагаахын 3-р курсын оюутан. Эхний жилүүдийн талаар юу ч асуугаарай.",
    lines: [
      { who: "Саарал чоно", text: "Анатоми үнэхээр бүгдийн ярьдаг шиг хэцүү юү?" },
      { who: "Та", text: "Тийм ээ. Гэхдээ хоёр дахь улирал илүү дээр. Автобусанд карт давтах хамгийн их тусалсан.", own: true },
      { who: "Саарал чоно", text: "Баярлалаа. Дотуур байрны талаар бас асууж болох уу?" },
      { who: "Та", text: "Мэдээж, энд л асуугаарай.", own: true },
    ],
  },
};

const en: Demo = {
  knot: {
    name: "Calculus · exam prep",
    meta: "Real names · Requests approved",
    lines: [
      { who: "Nomin", text: "Let's take seats on the library's third floor at 9 tomorrow. They say it'll be −30 in the morning." },
      { who: "Temuulen", text: "Bus 7 is running late in the mornings. Leave before 8:30 and you'll make it." },
      { who: "Anu", text: "Sent a photo of my working for problem 4. Can someone check the limit?" },
      { who: "You", text: "Canteen after the exam?", own: true },
    ],
  },
  string: {
    them: "Night Bird",
    lines: [
      { who: "Night Bird", text: "The heating in our dorm is off again. Doing homework wrapped in a blanket." },
      { who: "You", text: "Ours too. Waiting for bus 7 at −30 in the morning is the worst part.", own: true },
      { who: "Night Bird", text: "Hold out till the Naadam break." },
      { who: "You", text: "I'm going to the countryside then. You?", own: true },
    ],
  },
  brane: {
    them: "Grey Wolf",
    emoji: "🩺",
    name: "Ask me about med school",
    bio: "Third-year med student. Ask me anything about the first years.",
    lines: [
      { who: "Grey Wolf", text: "Is anatomy really as hard as everyone says?" },
      { who: "You", text: "It is. The second term is better, though. Flashcards on the bus helped most.", own: true },
      { who: "Grey Wolf", text: "Thanks. Can I ask about the dorms too?" },
      { who: "You", text: "Of course, ask right here.", own: true },
    ],
  },
};

export const demoFor = (language: string | undefined): Demo => (demoLocale(language) === "en" ? en : mn);

/**
 * The address the closing block shows being verified. A shape, not a person, and
 * not a real institution: the domain is the app's own placeholder (request.domainHint).
 */
export const VERIFY_EXAMPLE = "bold@surguuli.edu.mn";
